import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Users, Award, Clock, Headphones } from "lucide-react";


/* ── IntersectionObserver hook ── */
const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [v, setV] = useState(false);

  useEffect(() => {
    const node = ref.current; // capture the current ref
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => setV(entry.isIntersecting),
      { threshold }
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node); // safely use captured node
    };
  }, [threshold]);

  return [ref, v];
};
  
/* ── Count-up ── */
const useCountUp = (end, active) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let v = 0; const step = end / 100;
    const t = setInterval(() => { v += step; if (v >= end) { setN(end); clearInterval(t); } else setN(Math.floor(v)); }, 18);
    return () => clearInterval(t);
  }, [active, end]);
  return n;
};

const Stat = ({ num, suf = "+", label, icon: Icon, active }) => {
  const c = useCountUp(num, active);
  return (
    <div className="st">
      <div className="st-ic"><Icon size={18} /></div>
      <div className="st-n">{c.toLocaleString()}{suf}</div>
      <div className="st-l">{label}</div>
    </div>
  );
};

/* ── Data ── */
const numbered = [
  { n: "01", title: "Expert IT Team", body: "Our multi-disciplined engineers, architects, and consultants seamlessly handle the full complexity of modern IT — ensuring solutions as versatile as your business needs." },
  { n: "02", title: "Cutting-Edge Technology", body: "We leverage the latest cloud platforms, security frameworks, and development stacks to redefine what's possible — keeping your business at the forefront of innovation." },
  { n: "03", title: "End-to-End Ownership", body: "From initial scoping to post-launch support, we take complete ownership of every engagement. You ship faster, with fewer risks and a team that truly has your back." },
];


/* Orbit items — service icons arranged in a spiral */
const orbitItems = Array.from({ length: 12 }).map((_, i) => {
  const data = [
    { e: "⚙️", label: "Automation" },
    { e: "☁️", label: "Cloud" },
    { e: "🔒", label: "Cyber Security" },
    { e: "📱", label: "Mobile Apps" },
    { e: "📊", label: "Analytics" },
    { e: "🛠️", label: "DevOps" },
    { e: "💻", label: "Web Dev" },
    { e: "🌐", label: "Networking" },
    { e: "🧩", label: "Integration" },
    { e: "📡", label: "IoT" },
    { e: "⚡", label: "Performance" },
    { e: "🔐", label: "Encryption" },
  ];

  const angle = (i / data.length) * Math.PI * 2;

  return {
    ...data[i],
    angle,
  };
});

export default function Home() {
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.35 });
  const heroRef = useRef(null);
  const [statsRef, statsIn] = useInView(0.2);
  const [numbRef, numbIn] = useInView(0.1);

  const [orbitRef, orbitIn] = useInView(0.1);

  /* Splash: title animates in after small delay */


  /* Mouse glow */
  useEffect(() => {
    const fn = (e) => {
      if (!heroRef.current) return;
      const r = heroRef.current.getBoundingClientRect();
      setMouse({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
    };
    window.addEventListener("mousemove", fn);
    return () => window.removeEventListener("mousemove", fn);
  }, []);

  return (
    <div className="hp">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Instrument+Serif:ital@0;1&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        :root{
          --blue:#2563eb;
          --blue2:#3b82f6;
          --black:#080808;
          --dark:#0f0f0f;
          --gray:#f0f0f0;
          --lgray:#f7f7f7;
          --white:#ffffff;
          --txd:#111111;
          --txm:#666666;
          --txl:#999999;
          --bd:rgba(0,0,0,.08);
          --bdd:rgba(255,255,255,.08);
          --f:'Inter',sans-serif;
          --serif:'Instrument Serif',serif;
        }
        .hp{font-family:var(--f);overflow-x:hidden;background:var(--black)}

        /* ═══════════════════════════════════════
           HERO  — pure black
        ═══════════════════════════════════════ */
        .hero{
          position:relative;min-height:100vh;background:var(--black);
          display:flex;flex-direction:column;align-items:center;justify-content:center;
          text-align:center;padding:8rem 1.5rem 6rem;overflow:hidden;
          background:
    linear-gradient(rgba(6,7,14,0.75), rgba(6,7,14,0.9)), /* dark overlay */
    url('https://www.openaccessgovernment.org/wp-content/uploads/2022/09/dreamstime_xxl_171995839-1068x600.jpg');
        }

        /* subtle dot-grid background */
        .hero-dots{
          position:absolute;inset:0;z-index:0;
          background-image:radial-gradient(circle,rgba(255,255,255,.07) 1px,transparent 1px);
          background-size:32px 32px;
          mask-image:radial-gradient(ellipse 80% 70% at 50% 50%,#000 30%,transparent 100%);
        }

        /* blue glow at top */
        .hero-glow-top{
          position:absolute;top:-120px;left:50%;transform:translateX(-50%);
          width:800px;height:400px;border-radius:50%;
          background:radial-gradient(ellipse,rgba(37,99,235,.25),transparent 65%);
          filter:blur(40px);z-index:0;pointer-events:none;
          animation:glowPulse 6s ease-in-out infinite;
        }
        @keyframes glowPulse{0%,100%{opacity:.6;transform:translateX(-50%) scale(1)}50%{opacity:1;transform:translateX(-50%) scale(1.08)}}

        /* mouse-tracking glow */
        .hero-cursor-glow{
          position:absolute;width:600px;height:600px;border-radius:50%;
          background:radial-gradient(circle,rgba(59,130,246,.08),transparent 65%);
          pointer-events:none;transform:translate(-50%,-50%);
          transition:left 1.2s cubic-bezier(.25,.46,.45,.94),top 1.2s cubic-bezier(.25,.46,.45,.94);
          filter:blur(20px);z-index:0;
        }

        /* thin horizontal line accent */
        .hero-line{
          position:absolute;top:0;left:0;right:0;height:1px;
          background:linear-gradient(90deg,transparent 0%,rgba(37,99,235,.6) 50%,transparent 100%);
          z-index:1;animation:lineIn 1s ease-out .1s both;
        }
        @keyframes lineIn{from{opacity:0;transform:scaleX(0)}to{opacity:1;transform:scaleX(1)}}

        .hero-con{position:relative;z-index:1;max-width:820px}

        /* pill badge */
        .badge{
          display:inline-flex;align-items:center;gap:8px;
          background:rgba(255,255,255,.05);
          border:1px solid rgba(255,255,255,.12);
          border-radius:100px;padding:.42rem 1.1rem;
          font-size:.68rem;font-weight:600;letter-spacing:.12em;text-transform:uppercase;
          color:rgba(255,255,255,.5);margin-bottom:2rem;
          opacity:0;animation:fadeUp .7s ease .3s both;
        }
        .badge-dot{width:5px;height:5px;border-radius:50%;background:var(--blue2);animation:blink 2s infinite}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}

        /* MAIN TITLE — staggered word reveal */
        .hero-title{
          font-size:clamp(2.6rem,5.5vw,5rem);
          font-weight:900;line-height:1.02;
          letter-spacing:-.045em;color:#fff;
          margin-bottom:1.5rem;
        }
        .hero-title .line{display:block;overflow:hidden}
        .hero-title .word{
          display:inline-block;
          opacity:0;transform:translateY(100%);
          animation:wordReveal .8s cubic-bezier(.16,1,.3,1) both;
        }
        .hero-title .word.blue{
          background:linear-gradient(135deg,#93c5fd,#3b82f6);
          -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
        }
        @keyframes wordReveal{to{opacity:1;transform:translateY(0)}}

        .hero-sub{
          font-size:1rem;font-weight:400;line-height:1.75;
          color:rgba(255,255,255,.42);max-width:500px;margin:0 auto 2.8rem;
          opacity:0;animation:fadeUp .8s ease .9s both;
        }

        .hero-btns{
          display:flex;align-items:center;justify-content:center;gap:.9rem;flex-wrap:wrap;
          opacity:0;animation:fadeUp .8s ease 1.1s both;
        }
        .btn-solid{
          display:inline-flex;align-items:center;gap:.5rem;
          background:var(--blue);color:#fff;border:none;border-radius:8px;
          padding:.82rem 1.65rem;font-family:var(--f);font-size:.85rem;font-weight:700;
          cursor:pointer;transition:all .25s ease;text-decoration:none;
          box-shadow:0 0 0 0 rgba(37,99,235,0);
        }
        .btn-solid:hover{background:#1d4ed8;transform:translateY(-3px);box-shadow:0 12px 32px rgba(37,99,235,.5)}
        .btn-ghost{
          display:inline-flex;align-items:center;gap:.5rem;
          background:transparent;border:1px solid rgba(255,255,255,.15);
          color:rgba(255,255,255,.65);border-radius:8px;padding:.82rem 1.65rem;
          font-family:var(--f);font-size:.85rem;font-weight:600;
          cursor:pointer;transition:all .25s ease;text-decoration:none;
        }
        .btn-ghost:hover{background:rgba(255,255,255,.07);color:#fff;transform:translateY(-3px)}

        /* floating glass cards */
        .hero-floats{position:absolute;inset:0;pointer-events:none;z-index:1}
        .hfc{
          position:absolute;display:flex;align-items:center;gap:.7rem;
          background:rgba(255,255,255,.05);backdrop-filter:blur(12px);
          border:1px solid rgba(255,255,255,.09);border-radius:12px;
          padding:.75rem 1rem;color:#fff;font-size:.77rem;font-weight:600;
          white-space:nowrap;box-shadow:0 4px 24px rgba(0,0,0,.4);
          opacity:0;animation:fadeUp .8s ease both;
        }
        .hfc-icon{width:28px;height:28px;border-radius:7px;background:rgba(37,99,235,.3);display:flex;align-items:center;justify-content:center;font-size:.85rem}
        .hfc-sub{font-size:.6rem;color:rgba(255,255,255,.4);display:block;margin-bottom:1px}
        .hfc1{top:30%;left:4%;animation-delay:1.4s;animation-name:fadeUpLeft}
        .hfc2{top:26%;right:4%;animation-delay:1.6s;animation-name:fadeUpRight}
        .hfc3{bottom:24%;left:6%;animation-delay:1.8s;animation-name:fadeUpLeft}
        @keyframes fadeUpLeft{from{opacity:0;transform:translate(-20px,10px)}to{opacity:1;transform:translate(0,0)}}
        @keyframes fadeUpRight{from{opacity:0;transform:translate(20px,10px)}to{opacity:1;transform:translate(0,0)}}
        .hfc1,.hfc3{animation:floatA 7s ease-in-out infinite,fadeUpLeft .8s ease both}
        .hfc2{animation:floatB 8s ease-in-out infinite,fadeUpRight .8s ease both}
        @keyframes floatA{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
        @keyframes floatB{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}

        /* scroll line */
        .hero-scroll{
          position:absolute;bottom:2.5rem;left:50%;transform:translateX(-50%);
          display:flex;flex-direction:column;align-items:center;gap:6px;z-index:1;
          cursor:pointer;opacity:0;animation:fadeIn 1s ease 1.8s both;
        }
        .scroll-line{width:1px;height:44px;background:linear-gradient(to bottom,transparent,rgba(255,255,255,.4));animation:scrollDrop 2s ease-in-out infinite}
        @keyframes scrollDrop{0%{opacity:.2;transform:scaleY(0);transform-origin:top}50%{opacity:1;transform:scaleY(1)}100%{opacity:0;transform:scaleY(1);transform-origin:bottom}}
        .hero-scroll span{font-size:.58rem;letter-spacing:.2em;text-transform:uppercase;color:rgba(255,255,255,.3)}

        /* ═══════════════════════════════════════
           NUMBERED SECTION  — pure black (like Vyro)
        ═══════════════════════════════════════ */
        .numb-sec{
          background:var(--black);padding:0 1.5rem;
          border-top:1px solid rgba(255,255,255,.07);
        }
        .numb-sec .wrap{max-width:1100px;margin:0 auto}

        .numb-item{
          display:grid;grid-template-columns:260px 1fr;
          gap:4rem;align-items:start;
          padding:5rem 0;
          border-bottom:1px solid rgba(255,255,255,.06);
          opacity:0;transform:translateY(30px);
          transition:opacity .8s ease,transform .8s cubic-bezier(.16,1,.3,1);
        }
        .numb-item:last-child{border-bottom:none}
        .numb-item.vis{opacity:1;transform:translateY(0)}

        .numb-big{
          font-size:clamp(6rem,12vw,10rem);
          font-weight:900;line-height:.9;
          letter-spacing:-.06em;
          color:rgba(255,255,255,.08);
          font-family:var(--f);
          transition:color .3s ease;
          user-select:none;
        }
        .numb-item.vis:hover .numb-big{color:rgba(255,255,255,.18)}
        .numb-item.vis:hover .numb-big.active{color:rgba(255,255,255,.9)}
        .numb-big.active{color:rgba(255,255,255,.85)}

        .numb-right{}
        .numb-title{
          font-size:clamp(1.3rem,2.2vw,1.85rem);
          font-weight:700;color:#fff;
          letter-spacing:-.03em;line-height:1.2;
          margin-bottom:1.1rem;margin-top:.5rem;
        }
        .numb-body{
          font-size:.95rem;line-height:1.8;
          color:rgba(255,255,255,.45);font-weight:400;
          max-width:520px;
        }

        /* ═══════════════════════════════════════
           STATS  — very dark
        ═══════════════════════════════════════ */
        .stats-sec{
          background:#0a0a0a;
          border-top:1px solid rgba(255,255,255,.06);
          border-bottom:1px solid rgba(255,255,255,.06);
          padding:5rem 1.5rem;
        }
        .stats-grid{
          display:grid;grid-template-columns:repeat(4,1fr);
          max-width:900px;margin:0 auto;
          border:1px solid rgba(255,255,255,.07);
          border-radius:16px;overflow:hidden;background:rgba(255,255,255,.07);gap:1px;
        }
        .st{background:#0a0a0a;padding:2.5rem 1.5rem;text-align:center;transition:background .3s}
        .st:hover{background:rgba(37,99,235,.07)}
        .st-ic{color:var(--blue2);margin-bottom:.75rem;display:flex;justify-content:center;opacity:.55}
        .st-n{font-size:2.5rem;font-weight:900;color:#fff;letter-spacing:-.06em;line-height:1;margin-bottom:.4rem}
        .st-l{font-size:.67rem;text-transform:uppercase;letter-spacing:.12em;color:rgba(255,255,255,.3);font-weight:600}
.orbit-label{
  position:absolute;
  bottom:-28px;
  left:50%;
  transform:translateX(-50%) scale(.8);
  font-size:.7rem;
  color:#fff;
  background:rgba(0,0,0,.7);
  padding:.25rem .5rem;
  border-radius:6px;
  white-space:nowrap;
  opacity:0;
  pointer-events:none;
  transition:.25s ease;
}

.orbit-icon:hover .orbit-label{
  opacity:1;
  transform:translateX(-50%) scale(1);
}
      

        /* ═══════════════════════════════════════
           ORBIT SECTION  — dark (Vyro icon spiral)
        ═══════════════════════════════════════ */
        .orbit-sec{
          background:var(--dark);padding:8rem 1.5rem;
          position:relative;overflow:hidden;
          border-top:1px solid rgba(255,255,255,.06);
        }
        .orbit-inner{
  position:relative;
  width:520px;
  height:520px; /* ✅ SAME as width */
  margin:0 auto;
}
          .orbit-rotator{
  position:absolute;
  inset:0;
  animation: rotateOrbit 25s linear infinite;
}

@keyframes rotateOrbit {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
          @keyframes rotateOrbit {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
        .orbit-center{
          position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
          text-align:center;z-index:2;
        }
        .orbit-center h2{
          font-size:clamp(1.8rem,3.5vw,3rem);font-weight:900;
          letter-spacing:-.04em;color:#fff;line-height:1.1;
        }
        .orbit-center p{font-size:.88rem;color:rgba(255,255,255,.4);margin-top:.6rem}

        .orbit-icon{
  position:absolute;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:50%;
  background:rgba(255,255,255,.05);
  border:1px solid rgba(255,255,255,.1);
  font-size:1.8rem; /* 🔥 bigger */
  width:72px;       /* 🔥 bigger */
  height:72px;
  cursor:pointer;
  transition:all .4s cubic-bezier(.34,1.56,.64,1);
  opacity:0;
}
        .orbit-icon.vis{opacity:1}
        .orbit-icon:hover{background:rgba(37,99,235,.2);border-color:rgba(37,99,235,.5);transform:scale(1.25) !important;box-shadow:0 8px 28px rgba(37,99,235,.35)}
@keyframes counterRotate {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(-360deg); }
}
        /* ═══════════════════════════════════════
           CTA  — pure black, simple
        ═══════════════════════════════════════ */
        .cta-sec{background:var(--black);padding:8rem 1.5rem;text-align:center;border-top:1px solid rgba(255,255,255,.07)}
        .cta-inner{max-width:700px;margin:0 auto}
        .cta-inner h2{
          font-size:clamp(2rem,4vw,3.5rem);
          font-weight:900;letter-spacing:-.045em;color:#fff;
          line-height:1.08;margin-bottom:1.25rem;
        }
        .cta-inner h2 em{font-style:italic;font-family:var(--serif);color:rgba(255,255,255,.55)}
        .cta-inner p{font-size:.95rem;color:rgba(255,255,255,.4);line-height:1.75;margin-bottom:2.5rem;max-width:480px;margin-left:auto;margin-right:auto}
        .cta-btns{display:flex;align-items:center;justify-content:center;gap:.85rem;flex-wrap:wrap}

        /* ═══════════════════════════════════════
           SHARED UTILITIES
        ═══════════════════════════════════════ */
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        .ctr{text-align:center}
        .wrap{max-width:1100px;margin:0 auto}

        /* ═══════════════════════════════════════
           RESPONSIVE
        ═══════════════════════════════════════ */
        @media(max-width:900px){
          .numb-item{grid-template-columns:120px 1fr;gap:2rem}
          .numb-big{font-size:5rem}
          .stats-grid{grid-template-columns:repeat(2,1fr)}
          .svc-grid{grid-template-columns:repeat(2,1fr)}
          .orbit-inner{
  position:relative;
  width:min(90vw, 520px);
  aspect-ratio:1/1; /* 🔥 keeps perfect square automatically */
  margin:0 auto;
}
          .hfc{display:none}
        }
        @media(max-width:600px){
          .numb-item{grid-template-columns:1fr;gap:.75rem;padding:3.5rem 0}
          .numb-big{font-size:4rem}
          .svc-grid{grid-template-columns:1fr}
          .stats-grid{grid-template-columns:1fr}
          .orbit-inner{
  position:relative;
  width:min(90vw, 520px);
  aspect-ratio:1/1; /* 🔥 keeps perfect square automatically */
  margin:0 auto;
}
        }
      `}</style>

      {/* ══════════════════════════════
          HERO — pure black, word-by-word reveal
      ══════════════════════════════ */}
      <section className="hero" ref={heroRef}>
        <div className="hero-dots" />
        <div className="hero-glow-top" />
        <div className="hero-cursor-glow" style={{ left: `${mouse.x * 100}%`, top: `${mouse.y * 100}%` }} />
        <div className="hero-line" />

        

        <div className="hero-con">
          <div className="badge"><div className="badge-dot" />IT Solutions & Digital Transformation</div>

          {/* Word-by-word staggered reveal */}
          <h1 className="hero-title">
            <span className="line">
              {"Shaping Tomorrow's".split(" ").map((w, i) => (
                <span key={i} className="word" style={{ animationDelay: `${.4 + i * .12}s`, marginRight: ".25em" }}>{w}</span>
              ))}
            </span>
            <span className="line">
              {"IT Landscape".split(" ").map((w, i) => (
                <span key={i} className="word blue" style={{ animationDelay: `${.7 + i * .12}s`, marginRight: ".25em" }}>{w}</span>
              ))}
              {" Today".split(" ").map((w, i) => (
                <span key={i} className="word" style={{ animationDelay: `${.95 + i * .12}s`, marginRight: ".25em" }}>{w}</span>
              ))}
            </span>
          </h1>

          <p className="hero-sub">
            We architect comprehensive IT ecosystems built on integrity, innovation, and excellence — driving your business into the digital future.
          </p>
          <div className="hero-btns">
            <a href="/contact" className="btn-solid">Start a Project <ArrowRight size={15} /></a>
            <a href="/services" className="btn-ghost">Explore Services</a>
          </div>
        </div>

        <div className="hero-scroll" onClick={() => document.getElementById("numb-sec")?.scrollIntoView({ behavior: "smooth" })}>
          <div className="scroll-line" />
          <span>Scroll</span>
        </div>
      </section>

      {/* ══════════════════════════════
          NUMBERED SECTION  — like Vyro's "1 / 2 / 3" layout
      ══════════════════════════════ */}
      <section className="numb-sec" id="numb-sec">
        <div className="wrap" ref={numbRef}>
          {numbered.map((item, i) => (
            <div
              key={i}
              className={`numb-item ${numbIn ? "vis" : ""}`}
              style={{ transitionDelay: `${i * .15}s` }}
            >
              <div className={`numb-big ${i === 1 ? "active" : ""}`}>{item.n}</div>
              <div className="numb-right">
                <div className="numb-title">{item.title}</div>
                <div className="numb-body">{item.body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════
          STATS — dark
      ══════════════════════════════ */}
      <section className="stats-sec">
        <div ref={statsRef} className="stats-grid">
          <Stat num={250} label="Happy Clients" icon={Users} active={statsIn} />
          <Stat num={450} label="Projects Done" icon={Award} active={statsIn} />
          <Stat num={15} label="Years Experience" icon={Clock} active={statsIn} />
          <Stat num={750} label="Tickets Resolved" icon={Headphones} active={statsIn} />
        </div>
      </section>

      

      {/* ══════════════════════════════
          ORBIT / SPIRAL  — dark (inspired by Vyro's app orbit)
      ══════════════════════════════ */}
      <section className="orbit-sec">
        <div className="orbit-inner" ref={orbitRef}>
  
  {/* ✅ STATIC CENTER (no rotation now) */}
  <div className="orbit-center">
    <h2>Check Out<br />Our Services</h2>
    <p>Everything your business needs</p>
  </div>

  {/* ✅ ONLY ICONS ROTATE */}
  <div className="orbit-rotator">
    {orbitItems.map((item, i) => {
     const radius = 40; // try 35–45
const x = 50 + Math.cos(item.angle) * radius;
const y = 50 + Math.sin(item.angle) * radius;

      return (
        <div
          key={i}
          className={`orbit-icon ${orbitIn ? "vis" : ""}`}
          style={{
            left: `${x}%`,
            top: `${y}%`,
            transform: orbitIn
              ? "translate(-50%, -50%) scale(1)"
              : "translate(-50%, -50%) scale(0)",
            transitionDelay: `${i * 0.05}s`,
          }}
        >
          {item.e}
          <span className="orbit-label">{item.label}</span>
        </div>
      );
    })}
  </div>

</div>
      </section>

      {/* ══════════════════════════════
          CTA — pure black, big type
      ══════════════════════════════ */}
      <section className="cta-sec">
        <div className="cta-inner">
          <h2>Ready to Build<br />Something <em>Great?</em></h2>
          <p>Let's talk about how WayForward IT can architect your digital future with precision and excellence.</p>
          <div className="cta-btns">
            <a href="/contact" className="btn-solid">Get Started <ArrowRight size={15} /></a>
            <a href="/services" className="btn-ghost">Our Services</a>
          </div>
        </div>
      </section>
    </div>
  );
  
}
